import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';

const CONSENT_VERSION = '1.0';

interface RegisterStudentPayload {
  display_name: string;
  school_year: string;
  country?: string;
  education_authority?: string;
  consent_account: boolean;
  consent_ai: boolean;
  consent_curriculum: boolean;
}

export function useRegisterStudent() {
  return useMutation({
    mutationFn: (payload: RegisterStudentPayload) =>
      apiFetch<{ child_id: string; display_name: string }>('/students', {
        method: 'POST',
        body: JSON.stringify({ ...payload, consent_version: CONSENT_VERSION }),
      }),
  });
}

export function useLinkStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invite_code: string) =>
      apiFetch<{ child_id: string; display_name: string }>('/parents/me/link-student', {
        method: 'POST',
        body: JSON.stringify({ invite_code }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parent', 'children'] }),
  });
}

/** New-academic-year reset (US-PRF-01/US-PRF-02) for a self-registered university
 * student updating their own country/study_level — mirrors
 * useUpdateAcademicContext in services/children.ts (see its comment for why every
 * one of these keys needs invalidating), pointed at the self route instead. */
export function useUpdateStudentAcademicContext(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      country_code: string;
      study_level: string;
      education_authorities?: string[];
    }) =>
      apiFetch<{ status: string; timetable_required: boolean }>(
        '/students/me/academic-context',
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'profile'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'timetable'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'free-time'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'proposals'] });
    },
  });
}
